<template>
    <div class="tw-calendar tw-stage tw-responsive__xs--half-padding tw-responsive__xxs--half-padding">
        <div v-if="calendarConfig.pageState === 'loading'">
            <loading-splash></loading-splash>
            <p class="tw-text--center">Loading available dates...</p>
        </div>

        <div v-if="calendarConfig.pageState === 'no_dates'">
            <p class="tw-text--center" v-if="dmnOptions.widgetMode === 'default-view' && events.length > 1">
                <previous-stage v-bind:twStyle="'tw-btn tw-btn__xs tw-btn--default'" v-bind:buttonStage='"calendar"'></previous-stage>
            </p>
            <div class="tw-item tw-item--muted">
                <p v-if="dmnOptions.widgetMode === 'default-view'" class="tw-text--center">{{this.soldOutMessage ? this.soldOutMessage : 'No dates available for this event'}}</p>
                <p v-if="dmnOptions.widgetMode === 'list-view' && selectedEvent.recurrenceModel.getTotalRemainingDates(this.today.clone()) === 1" class="tw-text--center">This is a single dated event</p>
            </div>
        </div>

        <div class="tw-calendar__container tw-responsive__xxs--gutter tw-responsive__xs--gutter tw-responsive__sm--gutter" v-if="calendarConfig.pageState === 'default'">
            <div class="tw-calendar__row tw-calendar__row--header">
                <div class="tw-calendar__item tw-calendar__control tw-text--middle tw-text-lg" v-if="calendarConfig.pageSize !== 'list'">
                    <button type="button" class="tw-btn__link" v-on:click="subtractMonth()" title="Go to previous month" v-show="calendarConfig.navPreviousPage">
                        <font-awesome-icon icon="chevron-left" /> Prev
                    </button>
                </div>
                <h3 v-if="dmnOptions.calendarDisplay !== 'list-view'" class="tw-calendar__item tw-calendar__header tw-text--center tw-text--middle tw-responsive__xs--half-padding tw-responsive__xxs--half-padding">
                    {{ calendarConfig.pageSize !== 'list' ? dateContext.format(calendarConfig.getHeaderDateFormat(calendarConfig.pageSize)) : 'Available Dates' }}
                </h3>
                <div class="tw-calendar__item tw-calendar__control tw-text--right tw-text--middle tw-text-lg tw-responsive__xxs--half-padding tw-responsive__xs--half-padding" v-if="calendarConfig.pageSize !== 'list'">
                    <button type="button" class="tw-btn__link" v-on:click="addMonth()" title="Go to next month" v-show="calendarConfig.navNextPage">
                        Next <font-awesome-icon icon="chevron-right" />
                    </button>
                </div>
            </div>

            <div class="tw-calendar__row tw-calendar__row--header tw-text--center" v-if="selectedEvent.skip_sold_out_dates && !hasDatesAvailableInMonth && !hasGoneBack">
                <loading-splash></loading-splash>
                <p class="tw-text--center">These dates are all sold out, checking next month...</p>
            </div>

          <div v-if="calendarHeader && dmnOptions.calendarDisplay !== 'list-view'" class="tw-calendar__row">
                <div class="tw-calendar__item tw-calendar__header tw-text--center tw-responsive__xs--hide tw-responsive__xxs--hide tw-responsive__sm--hide tw-responsive__xxs--half-padding tw-responsive__xs--half-padding" v-for="headerText in calendarHeader.full" :key="headerText">
                    {{ headerText }}
                </div>
                <div class="tw-calendar__item tw-calendar__header tw-text--center tw-responsive__md--hide tw-responsive__lg--hide tw-responsive__xs__text--sm tw-responsive__xs--half-padding tw-responsive__xxs__text--sm tw-responsive__xxs--half-padding" v-for="headerText in calendarHeader.short" :key="headerText">
                    {{ headerText }}
                </div>
            </div>

            <p v-if="dmnOptions.widgetMode === 'default-view' && events.length > 1" class="tw-text--center">
                <previous-stage v-bind:twStyle="'tw-btn tw-btn__xs tw-btn--default'" v-bind:buttonStage='"calendar"'></previous-stage>
            </p>

            <!-- DEFAULT CALENDAR VIEW -->
            <div v-if="dmnOptions.calendarDisplay !== 'list-view'">
                <!-- WAITLIST/LINKED OCCURRENCE INFO BOXES -->
                <wait-list-widget v-if="dmnOptions.showWaitList && selectedCalendarItem.toggleWaitListWidget" v-bind:calendarItem="selectedCalendarItem"></wait-list-widget>
                <linked-occurrence v-if="selectedCalendarItem.linkedOccurrence && selectedCalendarItem.toggleLinkedOccurrence" v-bind:linkedOccurrence="selectedCalendarItem.linkedOccurrence"></linked-occurrence>

                <div class="tw-calendar__row" v-for="(calendarRow, index) in calendarStructure" :key="index">
                    <div class="tw-item tw-item__day tw-calendar__item tw-responsive__xs--half-padding"
                        v-for="calendarItem in calendarRow"
                        :key="calendarItem.dateFriendly"
                        v-bind:class="getCssClasses(calendarItem)"
                        v-on:click="handleCalendarItemSelected(calendarItem)"
                    >
                        <p class="tw-info tw-responsive__xxs--center tw-responsive__xs--center tw-responsive__sm--center"
                            v-bind:class="{
                                'tw-responsive__xs__text--md': calendarConfig.pageSize === 'list',
                                'tw-text--md': dmnOptions.widgetMode === 'list-view',
                                'tw-text--lg': dmnOptions.widgetMode === 'default-view'
                            }">
                            {{ calendarItem.dateFriendly }}
                            <span class="tw-responsive__md--hide tw-responsive__lg--hide" v-if="calendarItem.linkedOccurrence">
                                <font-awesome-icon v-if="calendarItem.linkedOccurrence.icon && calendarItem.linkedOccurrence.icon === 'calendar'" :icon="['far', 'calendar-alt']" />
                                <font-awesome-icon v-if="calendarItem.linkedOccurrence.icon && calendarItem.linkedOccurrence.icon === 'commenting'" :icon="['fas', 'comment-dots']" />
                                <font-awesome-icon v-if="calendarItem.linkedOccurrence.icon && calendarItem.linkedOccurrence.icon === 'star'" :icon="['fas', 'star']" />
                                <font-awesome-icon v-if="calendarItem.linkedOccurrence.icon && calendarItem.linkedOccurrence.icon === 'bullhorn'" :icon="['fas', 'bullhorn']" />
                                <font-awesome-icon v-if="calendarItem.linkedOccurrence.icon && calendarItem.linkedOccurrence.icon === 'certificate'" :icon="['fas', 'certificate']" />
                            </span>
                        </p>
                        <p class="tw-info tw-info--top tw-info--right tw-responsive__xxs--hide tw-responsive__xs--hide tw-responsive__sm--hide" v-if="!calendarItem.offSale && calendarItem.isValidDate && calendarItem.lowestPrice !== 'From Free'">
                            <span class="tw-info tw-info--top tw-info--right tw-responsive__xxs--hide tw-responsive__xs--hide tw-responsive__sm--hide" v-if="selectedEvent.getPriceRange(calendarItem.dateMoment).length > 1">From</span>
                            {{ calendarItem.lowestPrice | currency }}
                        </p>
                        <p class="tw-info tw-info--top tw-info--right tw-responsive__xxs--hide tw-responsive__xs--hide tw-responsive__sm--hide" v-if="!calendarItem.offSale && calendarItem.isValidDate && calendarItem.lowestPrice === 'From Free'">{{ calendarItem.lowestPrice }}</p>
                        <p class="tw-info tw-info--top tw-info--right tw-responsive__xxs--hide tw-responsive__xs--hide tw-responsive__sm--hide" v-if="calendarItem.offSale && !calendarItem.linkedOccurrence">{{ getOffSaleReason(calendarItem.dateMoment) }}</p>

                        <!-- Wait List Widget -->
                        <button type="button" class="tw-btn tw-btn__wait-list tw-btn--secondary tw-btn--md tw-btn--block tw-responsive__sm--hide tw-responsive__xs--hide" v-if="calendarItem.offSale && calendarItem.offSale === 'Sold Out' && dmnOptions.showWaitList && !calendarItem.linkedOccurrence">
                            Join Wait List
                        </button>

                        <!-- Linked Occurrences -->
                        <button type="button" class="tw-btn tw-btn--primary tw-btn--md tw-btn--block tw-responsive__sm--hide tw-responsive__xs--hide" v-if="calendarItem.linkedOccurrence">
                            {{calendarItem.linkedOccurrence.button_text}}
                        </button>
                    </div>
                </div>
            </div>

            <!-- List CALENDAR VIEW -->
            <div v-if="dmnOptions.widgetMode === 'list-view' && dmnOptions.calendarDisplay === 'list-view'" class="tw-accordion-calendar">
                <div class="tw-calendar__row" v-for="(calendarRow, index) in calendarStructure" :key="index">
                    <div class="tw-item tw-item__day tw-calendar__item tw-responsive__xs--half-padding"
                        v-for="calendarItem in calendarRow"
                        :key="calendarItem.dateFriendly"
                        v-bind:class="getCssClasses(calendarItem)"
                        v-on:click="handleCalendarItemSelected(calendarItem)"
                    >
                        <p class="tw-info tw-text--lg tw-responsive__xxs--center tw-responsive__xs--center" v-bind:class="{'tw-responsive__xs__text--md': calendarConfig.pageSize === 'list'}">{{ calendarItem.dateFriendly }}</p>
                        <p class="tw-info tw-info--bottom tw-info--right tw-responsive__xxs--hide tw-responsive__xs--hide tw-responsive__sm--hide" v-if="!calendarItem.offSale && calendarItem.isValidDate">From {{ calendarItem.lowestPrice | currency }}</p>
                    </div>
                </div>
            </div>
        </div><br>

        <p v-if="event.tickets_available_at_door && showLimitedTicketsOnDoorMessage" class="tw-responsive__xs--hide tw-responsive__xxs--hide tw-responsive__sm--hide"> *Limited tickets available on the door.</p>

        <ul class="tw-list--styleless tw-list--inline tw-responsive__md--hide tw-responsive__lg--hide">
            <li><span class="tw-label tw-label--primary">On Sale</span></li>
            <li><span class="tw-label tw-label--faded">Not On Sale</span></li>
            <li><span class="tw-label tw-label--dark">Cancelled</span></li>
            <li v-if="event.tickets_available_at_door"><span class="tw-label tw-label--danger">Online Sold Out / Limited Tickets On Door</span></li>
            <li v-if="!dmnOptions.showWaitList"><span class="tw-label tw-label--danger">Sold Out</span></li>
            <li v-if="dmnOptions.showWaitList"><span class="tw-label tw-label--purple">Sold Out / Wait List</span></li>
        </ul>
    </div>
</template>

<script lang='ts'>
    import { Vue } from "vue-property-decorator";
    import { Component } from "vue-property-decorator";
    import Resources from 'designmynight-javascript-sdk/src/Integrations/vue.js';
    import moment from 'moment-timezone';
    import Event from '../../../models/Event';
    import Occurrence from '../../../models/Occurrence';
    import CalendarConfig from '../../../constants/CalendarConfig';
    import CalendarItem from '../../../models/CalendarItem';
    import TagOptions from '../../../models/TagOptions';
    import QueryString from 'designmynight-javascript-sdk/src/Http/QueryString.js';
    import LinkedOccurrenceModel from '../../../models/LinkedOccurrence';

    //components
    import LoadingSplash from '../../misc/loading-splash/LoadingSplash.vue';
    import PreviousStage from '../../misc/previous-stage/PreviousStage.vue';
    import LinkedOccurrence from '../../misc/linked-occurrence/LinkedOccurrence.vue';
    import WaitListWidget from '../../misc/wait-list-widget/WaitListWidget.vue';
    import TicketWidgetComponent from '../../TicketWidgetComponent';

    //fontawesome
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import { faChevronLeft, faChevronRight, faCertificate, faStar, faBullhorn, faCommentDots } from '@fortawesome/free-solid-svg-icons';
    import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

    library.add(faChevronLeft, faChevronRight, faCalendarAlt, faCertificate, faStar, faBullhorn, faCommentDots);

    @Component({
        components: {
            LinkedOccurrence,
            FontAwesomeIcon,
            LoadingSplash,
            PreviousStage,
            WaitListWidget
        }
    })

    export default class CalendarStructure extends TicketWidgetComponent {
        // the event object the calendar is displaying dates for
        event: Event|any = {};

        occurrences: any = {};
        linkedOccurrences: any = {};

        // The fixed moment
        today: moment.Moment = moment();

        // the moment that changes when switching months
        dateContext: moment.Moment = moment();

        // calendar configuration class
        calendarConfig = new CalendarConfig('month', 'loading', false, true);

        // the calendar items/rows main object
        calendarStructure: any[] = [];

        // the header text that displays which days the
        calendarHeader: string|boolean = false;

        config: {
            listViewMax:number,
            occurrence:any
        } = {
            listViewMax: 8,
            occurrence: false
        }

        // a check to determine if the user has pressed backwards on the calendar
        hasGoneBack: boolean = false;

        // a check to determine if there is sellable dates on the current month
        hasDatesAvailableInMonth: boolean = true;

        // show a message saying that tickets are available on the door
        showLimitedTicketsOnDoorMessage: boolean = false;

        selectedCalendarItem: any|CalendarItem = {};

        soldOutMessage: string = '';

        // When mounted, state is loading until we find some occurrences
        mounted() {
            // The fixed moment
            this.today = moment();

            // the moment that changes when switching months
            this.dateContext = moment();

            // initializes the calendar structure and decides if a calendar should be displayed
            this.calendarOnLoad();

            // get the calendars occurrence data and populates the calendar with it
            this.getCalendarOccurrenceData();

            let windowEvent = window.document.createEvent('HTMLEvents');
            windowEvent.initEvent('resize', true, false);
            window.dispatchEvent(windowEvent);
        }

        /**
        * @desc sets up the initial load of the calendar view
        */
        calendarOnLoad () {
            let nextDate;

            // check this if this event has a recurrence
            if(!this.selectedEvent.recurrenceModel.hasRecurrence()) {
                let yesterday = moment().subtract(1, 'day');
                let startDate = this.selectedEvent.recurrenceModel.getNextDate(yesterday);

                // if there is no startDate and the recurrence is a multi_day event then get the startLimit
                if(!startDate && this.selectedEvent.recurrenceModel.frequency === 'multi_day') {
                    startDate = this.selectedEvent.recurrenceModel.getStartLimit();
                    this.chooseDate(startDate);
                }

                if(this.selectedEvent.recurrenceModel.getTotalRemainingDates(yesterday) < this.config.listViewMax) {
                    this.calendarConfig.pageSize = 'list';
                }
            }
            else if(this.config.occurrence || this.dmnOptions.occurrence) {
                this.config.occurrence = this.dmnOptions.occurrence;

                this.goToTicketStage(this.selectedOccurrence.occurrenceMoment);
            }
            else {
                let yesterday = moment().subtract(1, 'day');
                let remainingDates = this.selectedEvent.recurrenceModel.getTotalRemainingDates(yesterday);

                // if only one date remains, choose it automatically
                if(remainingDates === 1) {
                    if(nextDate = this.selectedEvent.recurrenceModel.getNextDate(yesterday)) {
                        this.calendarConfig.pageSize = 'list';
                    }
                }
                // if less dates remain than the list view max, set the list view page size
                else if(this.config.listViewMax && remainingDates > 1 && remainingDates < this.config.listViewMax){
                    this.calendarConfig.pageSize = 'list';
                }
                // set calendar page size based on recurrence
                else if(this.selectedEvent.recurrenceModel.frequency === 'month') {
                    this.calendarConfig.pageSize = 'year';
                }
                else {
                    this.calendarConfig.pageSize = 'month';
                }

                nextDate = nextDate || this.selectedEvent.recurrenceModel.getNextDate(yesterday);
                if(null !== this.selectedEvent.linked_occurrence_next_date){
                  nextDate = this.getNextDateForCalendar(
                      moment(this.selectedEvent.linked_occurrence_next_date),
                      nextDate
                  );
                }

                if(this.$root.$data.dateContext) {
                    this.setPageDate(this.$root.$data.dateContext);
                }
                else {
                    nextDate ? this.setPageDate(nextDate) : this.calendarConfig.pageState = 'no_dates';
                }
            }
        }

        getNextDateForCalendar(linkedOccurrenceDate:moment.Moment|any, nextDate:moment.Moment|any) {
          return linkedOccurrenceDate < nextDate ? linkedOccurrenceDate : nextDate;
        }


        /**
        * @desc handles getting occurrence data, setting up date ranges and checking for cached data
        */
        getCalendarOccurrenceData() {
            let pageStart = this.dateContext.clone();
            pageStart.startOf(this.calendarConfig.pageSize);

            let pageEnd: any;

            // if we are in a list and there is only one date, get that next date, else get the end limit
            if(this.calendarConfig.pageSize === 'list') {
                pageEnd = this.selectedEvent.recurrenceModel.getTotalRemainingDates(this.today.clone()) > 1 ? this.selectedEvent.recurrenceModel.getEndLimit() : this.selectedEvent.recurrenceModel.getNextDate(this.today.clone());
            }
            else {
                pageEnd = this.dateContext.clone();
                pageEnd.endOf(this.calendarConfig.pageSize);
            }

            // if we have an event ID, then check the $root for any cached occurrence data
            if(this.selectedEvent._id && this.$root.$data.selectedOccurrences && this.$root.$data.selectedOccurrences[this.selectedEvent._id]) {
                this.occurrences = this.$root.$data.selectedOccurrences[this.selectedEvent._id];

                if(this.$root.$data.linkedOccurrences[this.selectedEvent._id]) {
                    this.linkedOccurrences = this.$root.$data.linkedOccurrences[this.selectedEvent._id];
                }

                return this.handleOccurrenceDataResponse();
            }

            // if we have an event ID, then make concurrent requests for any occurrences or linkedOccurrences
            if(this.selectedEvent._id) {
                let dataArray = [
                    this.getOccurrenceData(pageStart, pageEnd)
                ]

                if(this.dmnOptions.linkedOccurrences) {
                    dataArray.push(this.getLinkedOccurrenceData(pageStart, pageEnd));
                }

                const promises = Promise.all(dataArray);

                promises.then(
                    (results) => this.handleOccurrenceDataResponse(),
                    (error) => {}
                )
            }
        }

        /**
        * @desc sends an API request for the occurrence data and handles where to go next
        */
        getOccurrenceData(pageStart: moment.Moment, pageEnd: moment.Moment) {
            //Allow for earlier events on the day before
            pageStart = pageStart.subtract(1, 'day');

            return this.dataProviderService.getOccurrenceData(pageStart, pageEnd).then(
                (data: any) => {
                    this.filterOccurrencesBySellUntil(data.occurrences)
                    this.occurrences = data.occurrences;
                    this.serverTime = moment(data.serverTime);
                    this.$root.$data.selectedOccurrences[this.selectedEvent._id] = this.occurrences;
                }
            );
        }

        /**
         * @desc get the last sell until date for an occurrence
         */
        getTicketDateSellUntil(occurrence: any) {
            const ticketTypes = occurrence.ticket_types;
            let sellUntil = moment(occurrence.sell_until);

            Object.keys(ticketTypes).forEach(key => {
                    const ticket = ticketTypes[key];
                    const ticketSellUntil =  moment(ticket.sell_until);
                    if (ticketSellUntil.isAfter(sellUntil)) {
                        sellUntil = ticketSellUntil;
                    }
                }
            );

            return sellUntil;
        }

        /**
         * @desc filter the occurrences by the sell until date
         */
        filterOccurrencesBySellUntil(occurrences: any) {
            let filteredOccurrences: {[key: string]: any} = {};

            Object.keys(occurrences).forEach((key: string) => {
                const occurrence = occurrences[key];
                const sellUntil = this.getTicketDateSellUntil(occurrence);

                if (sellUntil.isAfter(moment().startOf('day'))) {
                    filteredOccurrences[key] = occurrences[key];
                }
            });

            return filteredOccurrences;
        }

        /**
        * @desc sends an API request for the occurrence data and handles where to go next
        */
        getLinkedOccurrenceData(pageStart: moment.Moment, pageEnd?: moment.Moment) {
            return this.dataProviderService.getLinkedOccurrenceData(this.selectedEvent._id, pageStart, pageEnd).then(
                (data: any) => {

                    let linkedOccurrences = data.data;
                    for(let i = 0; i < linkedOccurrences.length; i++) {
                        if (moment(linkedOccurrences[i].item_date).isSameOrAfter(moment().startOf('day'))) {
                            this.linkedOccurrences[moment(linkedOccurrences[i].item_date).format('YYYYMMDD')] = linkedOccurrences[i];
                        }
                    }

                    this.$root.$data.linkedOccurrences[this.selectedEvent._id] = this.linkedOccurrences;
                }
            );
        }

        /**
        * @param {moment.Moment} pageMoment
        * @return {Promise<Occurrence>}
        */
        getOccurrencesForPage(pageMoment:moment.Moment) : Promise<Occurrence>|boolean {
            let pageEnd: moment.Moment;
            let pageStart: moment.Moment = pageMoment.clone();

            if(this.calendarConfig.pageSize !== 'list'){
                pageEnd = pageMoment.clone();
                pageStart.startOf(this.calendarConfig.pageSize);
                pageEnd.endOf(this.calendarConfig.pageSize);
            }
            else {
                pageEnd = this.selectedEvent.recurrenceModel.getEndLimit();
            }

            if(this.selectedEvent._id && this.$root.$data.selectedOccurrences && this.$root.$data.selectedOccurrences[this.selectedEvent._id]) {
                let dates = this.selectedEvent.recurrenceModel.getDatesInRange(pageStart, pageEnd);

                let isCached = false;

                for(let i = 0; i < dates.length; i++) {
                    let dateFormatted = dates[i].format('YYYYMMDD');

                    isCached = this.$root.$data.selectedOccurrences[this.selectedEvent._id][dateFormatted] ? true : false;
                }

                if(isCached) {
                    return true;
                }
            }
            if(this.dmnOptions.widgetMode === 'list-view') {
                this.getLinkedOccurrenceData(pageStart);
            } else {
                this.getLinkedOccurrenceData(pageStart, pageEnd);
            }

            //Allow events for the day before if allowing a sell after time
            pageStart = pageStart.subtract(1, 'day');
            return this.dataProviderService.getOccurrenceData(pageStart, pageEnd).then(
                (results: any) => {
                    for(let i = 0; i < this.calendarStructure.length; i++) {
                        for(let j = 0; j < this.calendarStructure[i].length; j++) {
                            this.calendarStructure[i][j].isValidDate = this.validateCalendarItemDate(this.calendarStructure[i][j].isValidDate, this.calendarStructure[i][j].dateMoment);
                            this.calendarStructure[i][j].offSale = this.getOffSaleReason(this.calendarStructure[i][j].dateMoment);
                        }
                    }

                    this.occurrences = {...this.occurrences,...results.occurrences};
                    this.$root.$data.selectedOccurrences[this.selectedEvent._id] = this.occurrences;

                    this.setCalendarMeta();
                    this.handleSkippingMonthIfNoDatesAvailable();
                }
            )
        };

        /**
        * @desc handles getting the initial occurrences request back from the API
        * @param {any} data
        */
        handleOccurrenceDataResponse() {
            // if there is only one occurrence then choose it as the date and continue to tickets
            if(
                Object.keys(this.occurrences).length === 1 &&
                this.selectedEvent.recurrenceModel.getTotalRemainingDates(this.today.clone().subtract('1', 'days')) === 1 &&
                !this.shouldDisplayLinkedOccurrences()
            ) {
                //if there is only one occurrence and the last stage was tickets, go back to events list
                if(
                    this.dmnOptions.widgetMode !== 'list-view' &&
                    this.$root.$data.config.fromStage &&
                    this.$root.$data.config.fromStage.name === 'tickets'
                ) {
                    this.$router.go(-1);
                }
                else {
                    // remove the calendar navigation breadcrumb if only one event
                    if (this.$root.$data.events.length === 1) {
                        delete this.$root.$data.stages['calendar'];
                    }

                    const occurrenceDate = moment(Object.keys(this.occurrences)[0]);
                    if (!this.showWaitListForSingleOccurrence(occurrenceDate)) {
                      // redirect to the tickets page
                      this.chooseDate(occurrenceDate);

                      if (this.getOffSaleReason(occurrenceDate) === 'Sold Out') {
                        this.soldOutMessage = 'This event is sold out';
                      }
                      this.calendarConfig.pageState = this.dmnOptions.widgetMode === 'list-view' ? 'default' : 'no_dates';
                    } else {
                      this.buildCalendar();
                      this.buildCalendarHeader();

                      this.calendarConfig.pageState = 'default';
                    }
                }
            }
            else if(
                this.selectedEvent.recurrenceModel.getTotalRemainingDates(this.today.clone()) > 0 ||
                this.selectedEvent.recurrenceModel.getTotalRemainingDates(this.today.clone()) === -1
            ) {
                // build the calendar structure
                this.buildCalendar();
                this.buildCalendarHeader();

                this.calendarConfig.pageState = 'default';
            }
            else {
                this.calendarConfig.pageState = 'no_dates';
            }
        }

        /**
        * @desc sets the date range of the page on the calendar we are currently viewing
        * @param {moment.Moment}
        */
        setPageDate(dateMoment:moment.Moment|any) {
            let pageSize = this.calendarConfig.pageSize;
            this.dateContext = dateMoment.clone();

            if(pageSize !== 'list') {
                let pageStart = dateMoment.clone();
                this.calendarConfig.pageStart = pageStart.startOf(pageSize);

                let pageEnd = dateMoment.clone();
                this.calendarConfig.pageEnd = pageEnd.endOf(pageSize);
            }
            else {
                this.calendarConfig.pageStart = dateMoment.clone();
                this.calendarConfig.pageEnd = this.selectedEvent.recurrenceModel.getEndLimit();
            }
        }

        showWaitListForSingleOccurrence(occurrenceDate:moment.Moment) {
          const dateString = occurrenceDate ? occurrenceDate.format('YYYYMMDD') : false;

          return this.dmnOptions.showWaitList && this.occurrences[dateString as string].is_sold_out;
        }

        /**
        * @desc initializes building of the calendar, switch statement chooses which format the calendar should be built in
        */
        buildCalendar() {
            // set calendar meta data
            this.setCalendarMeta();

            if(this.dmnOptions.widgetMode === 'default-view' || (this.dmnOptions.widgetMode === 'list-view' && this.dmnOptions.calendarDisplay !== 'list-view')) {
                switch(this.calendarConfig.pageSize) {
                    case 'month':
                        this.calendarStructure = this.buildCalendarByMonth();

                        if(this.selectedEvent.skip_sold_out_dates) {
                            this.hasDatesAvailableInMonth = this.datesAvailableInMonth();
                        }

                        break;

                    case 'year':
                        this.calendarStructure = this.buildCalendarByYear();
                        break;

                    case 'list':
                        this.calendarStructure = this.buildCalendarByList();
                        break;
                }
            }
            else {
                this.calendarConfig.pageSize = 'list';
                this.calendarStructure = this.buildCalendarByList(1);
            }

            // force the window to do a resize event so the widget loads correctly
            var windowEvent = window.document.createEvent('HTMLEvents');
            windowEvent.initEvent('resize', true, false);
            window.dispatchEvent(windowEvent);

            this.prepareAdjacentPages();
        }

        /**
        * @desc builds the calendar header text (M,T,W,T,F,S,S) as an object
        * @return {Object}
        */
        buildCalendarHeader() : {} {
            let calendarHeader: any = false;

            if(this.calendarConfig.pageSize === 'month'){
                calendarHeader = {
                    full: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
                    short: ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun']
                }
            }

            return this.calendarHeader = calendarHeader;
        };

        /**
        * @desc builds the calendar by monthly period
        * @return {Array}
        */
        buildCalendarByMonth() : any[] {
            let calendarRows = [];

            let startOfMonth = this.dateContext.clone();
            startOfMonth = startOfMonth.startOf('month').startOf('isoWeek');

            let endOfMonth = this.dateContext.clone();
            endOfMonth = endOfMonth.endOf('month').endOf('isoWeek');

            while(!startOfMonth.isAfter(endOfMonth, 'day')) {
              let startOfRange = startOfMonth.clone();
              let endOfRange = startOfMonth.clone();

              endOfRange = endOfRange.endOf('isoWeek');

              let calendarRow = this.buildCalendarRow(startOfRange, endOfRange);

              calendarRows.push(calendarRow);

              startOfMonth.add(1, 'week');
            }

            return calendarRows;
        }

        /**
        * @desc builds the calendar by yearly period
        * @return {Array}
        */
        buildCalendarByYear() : any[] {
            let calendarRows: any[] = [];

            let startDate = this.dateContext.clone();
            startDate.startOf('year');

            let endDate = this.dateContext.clone();
            endDate.endOf('year');

            let eventStartLimit: moment.Moment = this.selectedEvent.recurrenceModel.getStartLimit();

            if(eventStartLimit.isAfter(startDate, 'month')){
                startDate = eventStartLimit.clone();
                startDate.startOf('month');
            }

            let calendarDates = this.buildCalendarRow(startDate, endDate, true);

            // add linked occurrences to this list of dates
            if(this.linkedOccurrences) {
                calendarDates = this.addLinkedOccurrenceToCalendarRow(calendarDates);
            }

            // add an empty item to ensure even number of items
            if(calendarDates.length % 2 !== 0){
                calendarDates.push( {} );
            }

            // split calendar dates into rows
            while(calendarDates.length){
                let calendarRow = calendarDates.splice(0, 2);

                if(!this.isRowPast(calendarRow)) {
                    calendarRows.push(calendarRow);
                }
            }

            return calendarRows;
        }

        /**
        * @desc builds the calendar by the smaller list format
        * @return {Array}
        */
        buildCalendarByList(rowSize: any = 2) : any[] {
            let calendarRows = [];

            // start from yesterday
            let startDate = this.today.clone();
            startDate.subtract(1, 'days');

            let endDate = this.selectedEvent.recurrenceModel.getEndLimit();

            let calendarDates = this.buildCalendarRow(startDate, endDate, true);

            // add linked occurrences to this list of dates
            if(this.linkedOccurrences) {
                calendarDates = this.addLinkedOccurrenceToCalendarRow(calendarDates);
            }

            // add an empty item to ensure even number of items
            if(calendarDates.length % 2 !== 0){
                calendarDates.push( {} );
            }

            // split calendar dates into rows
            while(calendarDates.length){
                let calendarRow = calendarDates.splice(0, rowSize);

                if(!this.isRowPast(calendarRow)) {
                    calendarRows.push(calendarRow);
                }
            }

            return calendarRows;
        }

        /**
        * @desc builds the calendars rows(weeks)
        * @param {moment.Moment} dateFrom
        * @param {moment.Moment} dateTo
        * @param {boolean} onlyIncludeEventDates
        * @return {Array}
        */
        buildCalendarRow(dateFrom: moment.Moment, dateTo: moment.Moment, onlyIncludeEventDates:boolean = false) : any[] {
            let datesToGet = [];
            let ctrl = this;

            if(onlyIncludeEventDates) {
                datesToGet = this.selectedEvent.recurrenceModel.getDatesInRange(dateFrom, dateTo, '');
            }
            else {
                dateTo.add(1, 'day');

                while(dateFrom.isBefore( dateTo, 'day')) {
                    let nextDate = dateFrom.clone();
                    datesToGet.push(nextDate);

                    dateFrom.add(1, 'day');
                }
            }

            return datesToGet.reduce((calendarRow:any, dateToGet:any) => {
                    let calendarItem = ctrl.buildCalendarItem(dateToGet);
                    calendarRow.push(calendarItem);

                    return calendarRow;
                },
            [] );
        }

        /**
        * @desc builds the calendar items(specific dates)
        * @param {moment.Moment} itemMoment
        * @return {Object}
        */
        buildCalendarItem(itemMoment: moment.Moment) {
            let pageSize: string = this.calendarConfig.pageSize;
            let dateFriendly: string = itemMoment.format(this.calendarConfig.getItemDateFormat(pageSize));
            let calendarItem = new CalendarItem(itemMoment, dateFriendly, true);

            calendarItem.isValidDate = this.validateCalendarItemDate(calendarItem.isValidDate, itemMoment);
            calendarItem.offSale = this.getOffSaleReason(itemMoment);

            if(calendarItem.offSale === 'Sold Out' && this.dmnOptions.showWaitList) {
                calendarItem.showWaitList = true;
            }

            let startDate = this.dateContext.clone();
            startDate.startOf(this.calendarConfig.pageSize);

            let endDate = this.calendarConfig.pageSize === 'list' ? this.selectedEvent.recurrenceModel.getEndLimit() : this.dateContext.clone();

            if(endDate) {
                endDate.endOf(this.calendarConfig.pageSize);
                calendarItem.isThisPage = !itemMoment.isBefore(startDate, 'day') && !itemMoment.isAfter(endDate, 'day');
            }
            else {
                calendarItem.isThisPage = !itemMoment.isBefore(startDate, 'day');
            }

            let itemMomentDate = itemMoment.format("YYYYMMDD");
            let now = this.occurrences[itemMomentDate] ? this.getTicketDateSellUntil(this.occurrences[itemMomentDate]) : itemMoment;

            calendarItem.isPast = moment().isAfter(now, 'day');

            if(calendarItem.isValidDate) {
                this.getLowestPriceText(calendarItem, itemMoment);
            }

            // only if this isn't a valid date can we add a linked occurrence
            if(!calendarItem.isValidDate) {
                calendarItem = this.checkForLinkedOccurrences(calendarItem);
            }

            return calendarItem;
        };

        addLinkedOccurrenceToCalendarRow(calendarDates: any[]): any[] {
            for(let key in this.linkedOccurrences) {
                let checkDate = moment(key);

                let linkedOccurrence = new LinkedOccurrenceModel(this.linkedOccurrences[key]);

                if(linkedOccurrence.canDisplayTypeNow(this.serverTime)) {
                    // find where this date should appear
                    let calendarItem = this.buildCalendarItem(moment(checkDate))

                    // don't add a linked occurrence if a real event occurrence already exists on this date
                    let alreadyExistsOnDate = calendarDates.some((existingItem: any) =>
                        existingItem.dateMoment && existingItem.dateMoment.format('YYYYMMDD') === calendarItem.dateMoment.format('YYYYMMDD'));

                    if((calendarItem.isThisPage || this.calendarConfig.pageSize === 'list') && !alreadyExistsOnDate) {
                        calendarDates.push(calendarItem);
                    }

                    calendarDates.sort((a:any, b:any) =>
                        a.dateMoment < b.dateMoment ? -1 :
                        a.dateMoment > b.dateMoment ? 1 : 0);
                }
            }

            return calendarDates;
        }

        /**
        * @desc validates a calendar item to see if it should be displayed
        * @param {boolean} isValidDate, the current state of the calendar item
        * @param {moment.Moment} itemMoment, the date we are looking at
        * @return {boolean}
        */
        validateCalendarItemDate(isValidDate:boolean, itemMoment: moment.Moment) {
            let occurrenceString = itemMoment.format( 'YYYYMMDD' );
            isValidDate = this.selectedEvent.recurrenceModel.isValidDate(itemMoment, '');

            // check the occurrence exists
            if(this.occurrences[occurrenceString]) {

                // check not cancelled
                if(this.occurrences[occurrenceString].cancelled) {
                    isValidDate = false;
                }

                // check not sold out
                if(this.occurrences[occurrenceString].is_sold_out) {
                    isValidDate = false;
                }

                // check not on presale
                if(this.occurrences[occurrenceString].presale_mode_enabled) {
                    isValidDate = false;
                }
            }

            // if date is still valid, ensure there are sellable tickets
            isValidDate = isValidDate && this.selectedEvent.hasSellableTicketTypesOnDate(itemMoment);

            return isValidDate;
        }

        checkForLinkedOccurrences(calendarItem: CalendarItem): CalendarItem|any {
            if(this.linkedOccurrences[calendarItem.dateMoment.format('YYYYMMDD')]) {
                calendarItem.linkedOccurrence = new LinkedOccurrenceModel(this.linkedOccurrences[calendarItem.dateMoment.format('YYYYMMDD')]);

                calendarItem.linkedOccurrence = calendarItem.linkedOccurrence.canDisplayTypeNow(this.serverTime) ? calendarItem.linkedOccurrence : false;
            }

            return calendarItem;
        }

        /**
        * @desc gets the lowest price available on a date and returns formatted with currency
        * @param {CalendarItem} calendarItem
        * @param {moment.Moment} itemMoment
        */
        getLowestPriceText(calendarItem: CalendarItem, itemMoment: moment.Moment) {

            // set lowest price
            calendarItem.lowestPrice = this.selectedEvent.getLowestPrice(itemMoment);
            calendarItem.lowestPrice = calendarItem.lowestPrice === 0 ? 'Free' : calendarItem.lowestPrice;

            // if multiple prices append with 'From'
            let priceRange = this.selectedEvent.getPriceRange(itemMoment);

            if(priceRange.length > 1) {
                calendarItem.lowestPrice = 'From ' + calendarItem.lowestPrice;
            }
        }

        /**
        * @desc handles when a calendar item has been selected
        * @param {CalendarItem} calendarItem
        */
        handleCalendarItemSelected(calendarItem: CalendarItem) {
            // if the event is sold out and the Wait List is turned on, then show the Wait List widget for that date
            if(this.getOffSaleReason(calendarItem.dateMoment) === 'Sold Out' && this.dmnOptions.showWaitList && !calendarItem.linkedOccurrence) {
                this.$root.$on('closeWaitList', () => {
                    calendarItem.toggleWaitListWidget = false;
                });

                calendarItem.toggleWaitListWidget = !calendarItem.toggleWaitListWidget;

                this.selectedCalendarItem = calendarItem;

                return;
            }

            // if the widget is displaying a link to another page, display the tooltip
            if(calendarItem.linkedOccurrence) {
                this.$root.$on('closeLinkedOccurrence', () => {
                    calendarItem.toggleLinkedOccurrence = false;
                });

                calendarItem.toggleLinkedOccurrence = !calendarItem.toggleLinkedOccurrence;

                this.selectedCalendarItem = calendarItem;

                return;
            }

            // if we have selected a normal date
            this.chooseDate(calendarItem.dateMoment);
        }

        /**
        * @desc the date selected by the user
        * @param {moment.Moment} occurrenceDate
        */
        chooseDate(occurrenceDate: moment.Moment|any) {
            if(this.getOffSaleReason(occurrenceDate)) {
                return;
            }

            if(occurrenceDate && this.occurrences[occurrenceDate.format('YYYYMMDD')]) {
                this.$root.$data.selectedOccurrence = this.occurrences[occurrenceDate.format('YYYYMMDD')]

                this.$root.$data.cartManager.occurrence = this.selectedOccurrence;

                this.goToTicketStage(occurrenceDate);
            }
        };

        /**
        * @desc if we are in default-view, route to the tickets path, else emit an event to load the tickets up.
        * @param {moment.Moment} occurrenceDate
        */
        goToTicketStage(occurrenceDate: moment.Moment) {
            // Reset the cartManager incase there are still quantities selected from another date
            this.cartManager.resetCart();

            if(this.dmnOptions.widgetMode !== 'list-view') {
                // save what stage we were on
                this.$root.$data.dateContext = this.dateContext.clone();
            }

            if(this.dmnOptions.widgetMode === 'default-view') {
                this.$router.push({ path: 'tickets' });
            }
            else {
                this.$root.$emit('dateSelected', this.occurrences[occurrenceDate.format('YYYYMMDD')]);
            }
        }

        /**
        * @desc checks if the occurrence is off sale and returns the message
        * @param {moment.Moment} occurrence
        * @return {string}
        */
        getOffSaleReason(occurrence: moment.Moment) : string|null {
            let reason: string|null = null;
            let dateString = occurrence ? occurrence.format('YYYYMMDD') : false;

            if(dateString && this.occurrences[dateString]) {

                // check if cancelled
                if(this.occurrences[dateString].cancelled) {
                    reason = 'Cancelled';
                }
                // check if on sale
                else if(this.occurrences[dateString].status === 'private') {
                    reason = 'Not On Sale';
                }
                // check if sold out
                else if(this.occurrences[dateString].is_sold_out) {
                    reason = 'Sold Out';
                    this.showLimitedTicketsOnDoorMessage = true;
                }
                // check sell until time
                else if(this.occurrences[dateString].sell_until) {
                    let sellUntil = this.getTicketDateSellUntil(this.occurrences[dateString]);

                    // if sell until date is valid and after "now", then tickets no longer on sale
                    if(sellUntil.isValid() && this.serverTime.isAfter(sellUntil)) {
                        reason = "Not On Sale";
                    }
                }
            }

            return reason;
        }

        /**
        * @desc updates wether or not the calendar can go forward or backward
        * @param {Array} calendarRow
        * @return {Array}
        */
        isRowPast(calendarRow: any[]) : any[] {
            return calendarRow.reduce((isPast: any, item: any) => {
                return isPast && item.isPast;
            }, true);
        }

        /**
        * @desc updates wether or not the calendar can go forward or backward
        */
        setCalendarMeta() {
            if(this.calendarConfig.pageSize === 'list') {
                this.calendarConfig.navPreviousPage = false;
                this.calendarConfig.navNextPage = false;
            }
            else {
                this.calendarConfig.navNextPage = !!this.selectedEvent.recurrenceModel.getNextDate(this.calendarConfig.pageEnd);
                this.calendarConfig.navPreviousPage = !moment().isSame(this.calendarConfig.pageStart, 'month') && !!this.selectedEvent.recurrenceModel.getPreviousDate(this.calendarConfig.pageStart);

                // check if there are linked occurrences passed the month
                if(this.$root.$data.linkedOccurrences[this.selectedEvent._id] && Object.keys(this.$root.$data.linkedOccurrences[this.selectedEvent._id]).length > 0) {
                    for(let key in this.$root.$data.linkedOccurrences[this.selectedEvent._id]) {
                        let linkedOccurrenceDate = moment(this.$root.$data.linkedOccurrences[this.selectedEvent._id][key].item_date);

                        if(linkedOccurrenceDate.isAfter(this.calendarConfig.pageEnd)) {
                            this.calendarConfig.navNextPage = true;
                        }

                        if(linkedOccurrenceDate.isBefore(this.calendarConfig.pageStart)) {
                            this.calendarConfig.navPreviousPage = true;
                        }
                    }
                }
            }
        };

        /**
        * @desc If there are adjacent pages on the calendar download the occurrences data in advance
        */
        prepareAdjacentPages() {
            // prepare previous page occurrences
            if(this.calendarConfig.navPreviousPage && this.dateContext){
                let previousPage = this.dateContext.clone();
                previousPage = moment(previousPage).subtract(1, this.calendarConfig.pageSize);

                this.getOccurrencesForPage(previousPage);
            }

            // prepare next page occurrences
            if(this.calendarConfig.navNextPage && this.dateContext){
                let nextPage = this.dateContext.clone();
                nextPage = moment(nextPage).add(1, this.calendarConfig.pageSize);

                this.getOccurrencesForPage(nextPage);
            }
        };

        /**
        * @desc go forward a month on the calendar
        */
        addMonth () {
            if(this.calendarConfig.navNextPage) {
                let pageSize = this.calendarConfig.pageSize;

                this.dateContext = moment(this.dateContext).add(1, pageSize);

                this.setPageDate(this.dateContext);
                this.buildCalendar();
            }
        }

        /**
        * @desc go back a month on the calendar
        */
        subtractMonth () {
            if(this.calendarConfig.navPreviousPage) {
                let pageSize = this.calendarConfig.pageSize;

                this.hasGoneBack = true;
                this.dateContext = moment(this.dateContext).subtract(1, pageSize);

                this.setPageDate(this.dateContext);
                this.buildCalendar();
            }
        }

        /**
         * @desc returns the css classes that the calendar item should display
         */
        getCssClasses(calendarItem: CalendarItem) {
            let cssClasses = [];

            // if the item is in the past just add the hidden class
            if(calendarItem.isPast) {
                cssClasses.push('tw-item--pale');

                return cssClasses;
            }

            if(calendarItem.linkedOccurrence) {
                cssClasses.push('tw-item--selectable');

                return cssClasses;
            }

            // if the item is not on sale
            if(calendarItem.offSale && calendarItem.offSale !== 'Cancelled' && !calendarItem.showWaitList) {
                cssClasses.push('tw-item--danger');
            }

            // if the item should display the waitList
            if(calendarItem.offSale && calendarItem.offSale !== 'Cancelled' && calendarItem.showWaitList) {
                cssClasses.push('tw-item--purple');

                return cssClasses;
            }

            // if the item is cancelled
            if(calendarItem.offSale && calendarItem.offSale === 'Cancelled') {
                cssClasses.push('tw-item--dark');
            }

            // if the item isn't an occurrence at all
            if(!calendarItem.isValidDate && calendarItem.offSale !== 'Cancelled') {
                cssClasses.push('tw-item--pale');
            }

            // if the item is selectable
            if(calendarItem.isValidDate && !calendarItem.offSale) {
                cssClasses.push('tw-item--selectable');
            }

            // if the item is on a different page
            if(!calendarItem.isThisPage) {
                cssClasses.push('tw-item--faded');
            }
            return cssClasses.join(' ');
        }

        shouldDisplayLinkedOccurrences(): boolean {
            // if there are no linked occurrences then don't display
            if(Object.keys(this.linkedOccurrences).length === 0) {
                return false;
            }
            // if there are linked occurrences, return true if any are valid
            else {
                for(let key in this.linkedOccurrences) {
                    let linkedOccurrence = new LinkedOccurrenceModel(this.linkedOccurrences[key]);

                    if(linkedOccurrence.canDisplayTypeNow(this.serverTime)) {
                        // add back in the calendar stage if it was removed
                        this.$root.$data.stages['calendar'] = this.$root.$data.config.calendarStage;

                        return true;
                    }
                }
            }

            return false;
        }

        /**
         * @desc checks if there is dates available in this month
         * @return {bool}
         */
        private datesAvailableInMonth() {
            let validDatesInMonth = 0;

            for(let i = 0; i < this.calendarStructure.length; i++) {
                for(let j = 0; j < this.calendarStructure[i].length; j++) {
                    validDatesInMonth += this.calendarStructure[i][j].isValidDate && this.calendarStructure[i][j].isThisPage ? 1 : 0;
                }
            }

            return validDatesInMonth > 0;
        }

        /**
         * @desc if the event wants to skip over sold out dates, this will determine if the calendar should move to the next month
         */
        private handleSkippingMonthIfNoDatesAvailable() {
            if(this.selectedEvent.skip_sold_out_dates && this.calendarConfig.pageSize === 'month') {
                if(!this.datesAvailableInMonth()) {
                    this.addMonth();
                }
            }
        }
    }
</script>
